import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';
import { adminApi, type AdminTeamMember } from '@/lib/adminApi';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { ADMIN_AREAS, MANAGER_ROLES, ROLE_LABELS, STAFF_AREAS, STAFF_ROLES, type AdminArea } from '@/lib/adminAreas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Copy, KeyRound, Loader2, Power, ScrollText, Shield, ShieldOff, SlidersHorizontal, Trash2, UserPlus } from 'lucide-react';

const STATUS: Record<AdminTeamMember['status'], { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  ready: { label: 'Ready, not shared yet', className: 'bg-gray-700/50 text-gray-300 border-gray-600' },
  invited: { label: 'Waiting for them to set a password', className: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20' },
  disabled: { label: 'Switched off', className: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const AREA_LABELS: Record<string, string> = Object.fromEntries(ADMIN_AREAS.map((a) => [a.key, a.label]));

function fmtWhen(iso: string | null | undefined) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

function presetAreas(role: string): AdminArea[] {
  return STAFF_ROLES.find((r) => r.key === role)?.areas ?? [];
}

function sameAreas(a: string[], b: string[]) {
  return a.length === b.length && a.every((x) => b.includes(x));
}

interface SignInDetails {
  name: string;
  email: string;
  password: string;
}

/** Role dropdown plus the area checklist shown for staff roles. */
function AccessPicker({
  role,
  areas,
  allowDirector,
  onChange,
}: {
  role: string;
  areas: AdminArea[];
  allowDirector: boolean;
  onChange: (role: string, areas: AdminArea[]) => void;
}) {
  const staffRole = STAFF_ROLES.find((r) => r.key === role);
  const customised = staffRole && !sameAreas(areas, staffRole.areas);
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-gray-300">Role</Label>
        <Select value={role} onValueChange={(next) => onChange(next, presetAreas(next))}>
          <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
            <SelectValue placeholder="Choose a role" />
          </SelectTrigger>
          <SelectContent>
            {allowDirector && <SelectItem value="director">Director (full access, manages staff)</SelectItem>}
            {STAFF_ROLES.map((r) => (
              <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          {role === 'director'
            ? 'Directors see everything, including revenue and billing, and can add, change and switch off staff. Only you can add or change directors.'
            : staffRole?.summary}
        </p>
      </div>
      {staffRole && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-gray-300">What they can open</Label>
            {customised && (
              <button
                type="button"
                className="text-xs text-cyan-300 hover:text-cyan-200"
                onClick={() => onChange(role, staffRole.areas)}
              >
                Reset to {staffRole.label} defaults
              </button>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {STAFF_AREAS.map((area) => {
              const checked = areas.includes(area.key);
              return (
                <label
                  key={area.key}
                  className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                    checked ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(on) =>
                      onChange(role, on ? [...areas, area.key] : areas.filter((a) => a !== area.key))
                    }
                    className="mt-0.5"
                    aria-label={area.label}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-white">{area.label}</span>
                    <span className="block text-xs text-gray-500">{area.pages}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">
            Everyone also gets the Overview and their own Settings. Revenue, invoices, payments, deal prices after booking and the audit log stay with you and the directors. Access is checked by the server on every request, not just hidden in the menu.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The admin team: who has portal access, their role, what they can open and
 * where their account stands. The owner manages directors and staff,
 * directors manage staff, and staff see the list read-only.
 */
export default function TeamCard() {
  const { isOwner, isManager, canAccess } = useAdminAuth();
  const [members, setMembers] = useState<AdminTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState<string>(isOwner ? 'director' : 'operations');
  const [newAreas, setNewAreas] = useState<AdminArea[]>(isOwner ? [] : presetAreas('operations'));
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [details, setDetails] = useState<SignInDetails | null>(null);
  const [editing, setEditing] = useState<AdminTeamMember | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editAreas, setEditAreas] = useState<AdminArea[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getAdminTeam();
      setMembers(res.members);
    } catch (err: any) {
      toast.error(err.message || 'Could not load the team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canManage = (m: AdminTeamMember) =>
    isManager && m.role !== 'owner' && (isOwner || !MANAGER_ROLES.has(m.role));

  const handleCreate = async () => {
    setCreating(true);
    try {
      await adminApi.adminTeamAction({
        action: 'create',
        email: email.trim(),
        display_name: name.trim(),
        role: newRole,
        areas: newRole === 'director' ? undefined : newAreas,
      });
      toast.success(`${name.trim()}'s account is ready. Nothing is shared until you get their sign-in details.`);
      setName('');
      setEmail('');
      await load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const openEditor = (m: AdminTeamMember) => {
    setEditing(m);
    setEditRole(m.role);
    setEditAreas(MANAGER_ROLES.has(m.role) ? [] : ((m.areas ?? []) as AdminArea[]));
  };

  const saveAccess = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await adminApi.adminTeamAction({
        action: 'update',
        user_id: editing.user_id,
        role: editRole,
        areas: editRole === 'director' ? undefined : editAreas,
      });
      toast.success(`${editing.display_name.split(' ')[0]}'s access was updated. It applies from their next click.`);
      setEditing(null);
      await load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const run = async (member: AdminTeamMember, action: 'invite' | 'reset_two_step' | 'disable' | 'enable' | 'delete') => {
    const first = member.display_name.split(' ')[0];
    const confirmText: Partial<Record<typeof action, string>> = {
      invite:
        member.status === 'active'
          ? `Reset ${first}'s password? They lose access until they sign in with the new temporary password and choose their own.`
          : `Create one-time sign-in details for ${first}? Only share them when you are ready.`,
      reset_two_step: `Reset ${first}'s two-step sign-in? Only do this if you have spoken to them, for example because they lost their phone. They are signed out everywhere and set up their authenticator app again at their next sign-in.`,
      disable: `Switch off ${first}'s account? They are signed out of the portal and cannot sign in until it is switched back on.`,
      delete: `Delete ${first}'s unused account?`,
    };
    if (confirmText[action] && !confirm(confirmText[action])) return;

    setBusyId(member.user_id);
    try {
      const res = await adminApi.adminTeamAction({ action, user_id: member.user_id });
      if (action === 'invite' && res.temporary_password) {
        setDetails({ name: member.display_name, email: member.email ?? res.email ?? '', password: res.temporary_password });
      } else {
        toast.success(
          action === 'disable' ? `${first}'s account is switched off.`
            : action === 'enable' ? `${first}'s account is switched back on.`
              : action === 'reset_two_step' ? `${first}'s two-step sign-in was reset. They set it up again next time they sign in.`
                : `${first}'s account was deleted.`,
        );
      }
      await load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const portalUrl = `${window.location.origin}/admin`;
  const shareMessage = details
    ? [
      `Hi ${details.name.split(' ')[0]}, your H-Plate admin account is ready.`,
      '',
      `Sign in at ${portalUrl}`,
      `Email: ${details.email}`,
      `Temporary password: ${details.password}`,
      '',
      "You'll be asked to choose your own password the first time you sign in, then to set up an authenticator app on your phone (the iPhone Passwords app, Google Authenticator or Microsoft Authenticator). Have your phone with you.",
    ].join('\n')
    : '';

  const createDisabled =
    creating || name.trim().length < 2 || !email.includes('@') || (newRole !== 'director' && newAreas.length === 0);

  return (
    <Card className="bg-gray-900/50 border-gray-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-400" />
          Team
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-400">
          Everyone with access to the admin portal. Each person signs in with their own account, so the audit log shows who did what.
          {isOwner
            ? ' You add and manage directors and staff. Directors can add and manage staff, but not other directors.'
            : isManager
              ? ' You can add and manage staff. Only the owner can add or change directors.'
              : ' Ask a director if your access needs to change.'}
        </p>

        {isManager && (
          <>
            <div className="rounded-xl border border-gray-800 p-4 space-y-4">
              <p className="text-sm font-medium text-white">Add someone</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-gray-300">Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name+hplate@gmail.com"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              <AccessPicker
                role={newRole}
                areas={newAreas}
                allowDirector={isOwner}
                onChange={(role, areas) => {
                  setNewRole(role);
                  setNewAreas(areas);
                }}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-500 max-w-xl">
                  Use an email that is not already an H-Plate rider or driver login. Adding +hplate before the @ in a Gmail address creates a separate account while the mail still arrives in the same inbox. New accounts have no access until you get their sign-in details and they choose their own password.
                </p>
                <Button
                  onClick={handleCreate}
                  disabled={createDisabled}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Add {newRole === 'director' ? 'director' : (ROLE_LABELS[newRole] ?? 'staff').toLowerCase()}
                </Button>
              </div>
            </div>
            <Separator className="bg-gray-800" />
          </>
        )}

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 bg-gray-800/50 rounded" />
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {members.map((m) => {
              const status = STATUS[m.status];
              const busy = busyId === m.user_id;
              const manageable = canManage(m);
              const isStaff = !MANAGER_ROLES.has(m.role);
              return (
                <div key={m.user_id} className="py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-white text-sm font-medium">{m.display_name}</span>
                      <Badge
                        className={
                          m.role === 'owner'
                            ? 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                            : m.role === 'director'
                              ? 'bg-blue-500/10 text-blue-300 border-blue-500/20'
                              : 'bg-violet-500/10 text-violet-300 border-violet-500/20'
                        }
                      >
                        {ROLE_LABELS[m.role] ?? m.role}
                      </Badge>
                      {m.role !== 'owner' && <Badge className={status.className}>{status.label}</Badge>}
                      {m.status === 'active' && m.two_step !== undefined && (
                        <Badge
                          className={
                            m.two_step
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                          }
                        >
                          {m.two_step ? 'Two-step on' : 'Two-step not set up yet'}
                        </Badge>
                      )}
                    </div>
                    {m.email && <p className="text-xs text-gray-500 mt-0.5 truncate">{m.email}</p>}
                    {isStaff && m.areas && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {m.areas.map((a) => (
                          <span key={a} className="text-[11px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">
                            {AREA_LABELS[a] ?? a}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.actions_30d !== undefined && (
                      <p className="text-xs text-gray-500 mt-1">
                        Last sign-in {fmtWhen(m.last_sign_in_at)} · {m.actions_30d} action{m.actions_30d === 1 ? '' : 's'} in 30 days
                        {m.created_by_name ? ` · added by ${m.created_by_name}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {canAccess('audit') && (
                      <Link href={`/admin/audit?admin=${m.user_id}`}>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white h-8">
                          <ScrollText className="w-3.5 h-3.5 mr-1.5" /> Activity
                        </Button>
                      </Link>
                    )}
                    {manageable && (
                      <>
                        {m.status !== 'disabled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => openEditor(m)}
                            className="text-gray-400 hover:text-white h-8"
                          >
                            <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Access
                          </Button>
                        )}
                        {m.status !== 'disabled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => run(m, 'invite')}
                            className="border-gray-700 text-gray-300 hover:text-white h-8"
                          >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" />}
                            {m.status === 'ready' ? 'Get sign-in details' : m.status === 'invited' ? 'New sign-in details' : 'Reset password'}
                          </Button>
                        )}
                        {m.status !== 'disabled' && m.two_step && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => run(m, 'reset_two_step')}
                            className="text-gray-400 hover:text-amber-300 h-8"
                          >
                            <ShieldOff className="w-3.5 h-3.5 mr-1.5" /> Reset two-step
                          </Button>
                        )}
                        {m.status === 'disabled' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => run(m, 'enable')}
                            className="border-gray-700 text-emerald-400 hover:text-emerald-300 h-8"
                          >
                            <Power className="w-3.5 h-3.5 mr-1.5" /> Switch on
                          </Button>
                        ) : m.status !== 'ready' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => run(m, 'disable')}
                            className="text-gray-400 hover:text-red-400 h-8"
                          >
                            <Power className="w-3.5 h-3.5 mr-1.5" /> Switch off
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={busy}
                            onClick={() => run(m, 'delete')}
                            className="text-gray-400 hover:text-red-400 w-8 h-8"
                            aria-label={`Delete ${m.display_name}'s unused account`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.display_name}'s access</DialogTitle>
            <DialogDescription className="text-gray-400">
              Changes apply from their next click. They do not need to sign out.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <AccessPicker
              role={editRole}
              areas={editAreas}
              allowDirector={isOwner}
              onChange={(role, areas) => {
                setEditRole(role);
                setEditAreas(areas);
              }}
            />
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              disabled={saving || (editRole !== 'director' && editAreas.length === 0)}
              onClick={saveAccess}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!details} onOpenChange={(open) => !open && setDetails(null)}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sign-in details for {details?.name}</DialogTitle>
            <DialogDescription className="text-gray-400">
              This temporary password is shown once and is not saved anywhere. It stops working as soon as they choose their own. If you lose it, create new details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-[110px_1fr] gap-y-2">
              <span className="text-gray-500">Sign in at</span>
              <span className="font-mono text-gray-200 break-all">{portalUrl}</span>
              <span className="text-gray-500">Email</span>
              <span className="font-mono text-gray-200 break-all">{details?.email}</span>
              <span className="text-gray-500">Temporary password</span>
              <span className="font-mono text-emerald-300 text-base tracking-wider">{details?.password}</span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="border-gray-700 text-gray-300"
              onClick={() => setDetails(null)}
            >
              Done
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={async () => {
                await navigator.clipboard.writeText(shareMessage);
                toast.success('Copied. Send it to them privately, for example on WhatsApp.');
              }}
            >
              <Copy className="w-4 h-4 mr-2" /> Copy message to send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
